import { Subject, Observable } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

import {
  Input,
  Output,
  Component,
  OnDestroy,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';

import { SnAuditOperation, SnCameraGrid, SnCamera } from '@lib/api/entities';

import { SnCameraAuditService } from '../../services/camera-audit.service';

@Component({
  selector: 'sn-audit-camera-grid',
  templateUrl: './audit-camera-grid.container.html',
  styleUrls: ['./audit-camera-grid.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnAuditCameraGridContainer implements OnDestroy {
  @Input()
  public cameraGrid: SnCameraGrid;

  @Input()
  public hideBottom: boolean;

  @Input()
  public allowTimeout = true;

  @Input()
  public operationalView = false;

  @Input()
  public bulkSearch = false;

  @Output()
  public readonly orderChanged = new EventEmitter<SnCamera[]>();

  @Output()
  public readonly dateChange = new EventEmitter<Date>();

  @Output()
  public readonly gridSession = new EventEmitter<boolean>();

  private readonly _destroy$ = new Subject<void>();
  private readonly _cameraStreamStop$ = new Subject<string>();

  constructor(private readonly _cameraAuditService: SnCameraAuditService) {}

  public ngOnDestroy(): void {
    this._cameraStreamStop$.next();
    this._cameraStreamStop$.complete();
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onOrderChanged(cameras: SnCamera[]): void {
    this.orderChanged.emit(cameras);
  }

  public onCameraStreamPlay(data: { live: boolean; camera: SnCamera }): void {
    const auditOperation = data.live ? SnAuditOperation.ViewLive : SnAuditOperation.ViewRecord;

    this._cameraAuditService
      .startPlaying(data.camera, auditOperation)
      .pipe(takeUntil(this._selectedCameraStopped(data.camera.id)))
      .subscribe();
  }

  public onCameraStreamStop(camera: SnCamera): void {
    this._cameraAuditService
      .stopPlaying(camera.id)
      .subscribe(() => this._cameraStreamStop$.next(camera.id));
  }

  private _selectedCameraStopped(cameraId: string): Observable<string> {
    return this._cameraStreamStop$.pipe(filter((id) => !id || id === cameraId));
  }
}
